import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const AboutSection = styled.section`
  padding: 120px 0;
  background: ${props => props.theme.colors.dark};
  id: about;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 60px;
    text-align: center;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    gap: 40px;
  }
`;

const ImageContainer = styled(motion.div)`
  position: relative;
`;

const ProfileImage = styled.div`
  width: 100%;
  max-width: 400px;
  aspect-ratio: 1;
  border-radius: 20px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}20, ${props => props.theme.colors.secondary}20);
  position: relative;
  overflow: hidden;
  margin: 0 auto;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent 0%, ${props => props.theme.colors.primary}10 100%);
    z-index: 2;
  }
`;

const ProfileImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 20px;
`;

const Content = styled.div`
  max-width: 600px;
  
  @media (max-width: 1024px) {
    max-width: 100%;
    text-align: center;
    margin: 0 auto;
  }
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(90deg, ${props => props.theme.colors.primary}20, ${props => props.theme.colors.secondary}20);
  border: 1px solid ${props => props.theme.colors.primary}40;
  border-radius: 50px;
  padding: 8px 20px;
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
`;

const Title = styled(motion.h2)`
  font-family: ${props => props.theme.fonts.display};
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 24px;
  background: linear-gradient(135deg, ${props => props.theme.colors.white} 0%, ${props => props.theme.colors.primary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Description = styled(motion.p)`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.lightGray};
  line-height: 1.7;
  margin-bottom: 32px;
`;

const Achievements = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 40px;
  justify-items: center;

  @media (max-width: 1024px) {
    justify-items: center;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const Achievement = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.mediumGray}40, ${props => props.theme.colors.darkGray}60);
  border: 1px solid ${props => props.theme.colors.primary}30;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  width: 100%;
  max-width: 200px;
`;

const AchievementNumber = styled.div`
  font-family: ${props => props.theme.fonts.display};
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  line-height: 1;
  margin-bottom: 8px;
`;

const AchievementLabel = styled.div`
  color: ${props => props.theme.colors.lightGray};
  font-size: 0.9rem;
`;

const Credentials = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }
`;

const Credential = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.success}10, ${props => props.theme.colors.primary}10);
  border: 1px solid ${props => props.theme.colors.success}30;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.success};
  font-weight: 500;
`;

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const achievements = [
    { number: "7+", label: "Anos de Loja Física" },
    { number: "100%", label: "Validado na LM Motors" },
    { number: "LM", label: "Motors Fundada" },
    { number: "CCP", label: "Método Criado" }
  ];

  const credentials = [
    "Especialista em Consignação",
    "Mentor de Negócios",
    "Palestrante Nacional"
  ];

  return (
    <AboutSection ref={ref}>
      <Container>
        <ImageContainer
          initial={{ opacity: 0, x: -50 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <ProfileImage>
            <ProfileImg 
              src="/ideal para o perfil.jpg" 
              alt="Leonardo Celini - Especialista em CCP"
            />
          </ProfileImage>
        </ImageContainer>

        <Content>
          <Badge
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            CONHEÇA SEU MENTOR
          </Badge>

          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            LEONARDO CELINI
          </Title>

          <Description
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Criador do método CCP - Consignação com Propósito, com mais de 7 anos de loja física no mercado automotivo. 
            Leonardo desenvolveu esta metodologia 100% validada na LM Motors, baseada em resultados reais e comprovados.
          </Description>

          <Description
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            Especialista em consignação de veículos, Leonardo combina conhecimento técnico profundo 
            com estratégias práticas testadas em sua própria loja para ensinar outros lojistas.
          </Description>

          <Achievements
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {achievements.map((achievement, index) => (
              <Achievement key={index}>
                <AchievementNumber>{achievement.number}</AchievementNumber>
                <AchievementLabel>{achievement.label}</AchievementLabel>
              </Achievement>
            ))}
          </Achievements>

          <Credentials
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            {credentials.map((credential, index) => (
              <Credential key={index}>{credential}</Credential>
            ))}
          </Credentials>
        </Content>
      </Container>
    </AboutSection>
  );
};

export default About;